import { AdminMembersReportPageParams } from "@/admin/insights/member-reports/AdminMembersReportPage"
import AdminMembersReportFeedsTableFilterTags from "@/admin/insights/member-reports/tabs/discussions/AdminMembersReportFeedsTableFilterTags"
import AdminMembersReportFeedsTableRow, {
  AdminMembersReportFeedsTableRowSkeleton,
} from "@/admin/insights/member-reports/tabs/discussions/AdminMembersReportFeedsTableRow"
import AdminMembersReportFeedsTableToolbar, {
  AdminMembersReportFeedsTableToolbarSkeleton,
} from "@/admin/insights/member-reports/tabs/discussions/AdminMembersReportFeedsTableToolbar"
import {
  AdminMembersReportFeedsTablePaginationQuery,
  OrganizationFeedsOrderBy,
} from "@/admin/insights/member-reports/tabs/discussions/__generated__/AdminMembersReportFeedsTablePaginationQuery.graphql"
import { AdminMembersReportFeedsTableQuery } from "@/admin/insights/member-reports/tabs/discussions/__generated__/AdminMembersReportFeedsTableQuery.graphql"
import { AdminMembersReportFeedsTable_PaginationFragment$key } from "@/admin/insights/member-reports/tabs/discussions/__generated__/AdminMembersReportFeedsTable_PaginationFragment.graphql"
import { OrderDirection } from "@/admin/insights/member-reports/tabs/events/__generated__/AdminMembersReportEventsTablePaginationQuery.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { ProductFilterDropdownContentState } from "@components/dropdown/product/ProductFilterDropdownContent"
import { DiscoTable, DiscoTableSkeletonWithHeader, DiscoText } from "@disco-ui"
import { DiscoTableToolbarStateWithSort } from "@disco-ui/table/header/search/DiscoTableSortDropdown"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import { useState } from "react"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"

export type AdminMembersReportFeedsTableToolbarState = DiscoTableToolbarStateWithSort &
  ProductFilterDropdownContentState

const FEEDS_PER_PAGE = 10

const headers = [
  { value: "Feed Name" },
  { value: "Location" },
  { value: "Comments" },
  { value: "Reactions" },
]

function AdminMembersReportFeedsTable(): JSX.Element | null {
  const [queryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const [activePage, setActivePage] = useState(1)
  const [toolbarState, setToolbarState] =
    useState<AdminMembersReportFeedsTableToolbarState>({
      allProducts: { value: "all", title: "All" },
      products: [],
      allCommunity: null,
      sort: {
        id: "none",
        order: { field: "name" },
        title: "Sort",
      },
      direction: "ASC",
    })

  const classes = useStyles()
  const activeOrganization = useActiveOrganization()!

  const { node } = useLazyLoadQuery<AdminMembersReportFeedsTableQuery>(
    graphql`
      query AdminMembersReportFeedsTableQuery(
        $id: ID!
        $first: Int
        $after: String
        $last: Int
        $before: String
        $memberOrganizationMembershipId: ID!
        $canRead: Boolean
        $productIds: [ID!]
        $excludeProductLevel: Boolean
        $orderBy: OrganizationFeedsOrderBy
        $direction: OrderDirection
      ) {
        node(id: $id) {
          ... on Organization {
            id
            ...AdminMembersReportFeedsTable_PaginationFragment
              @arguments(
                first: $first
                after: $after
                last: $last
                before: $before
                memberOrganizationMembershipId: $memberOrganizationMembershipId
                canRead: $canRead
                productIds: $productIds
                excludeProductLevel: $excludeProductLevel
                orderBy: $orderBy
                direction: $direction
              )
          }
        }
      }
    `,
    {
      id: activeOrganization.id,
      first: FEEDS_PER_PAGE,
      memberOrganizationMembershipId: queryParams.om!,
      canRead: !activeOrganization.viewerIsOwnerOrAdmin,
      productIds: toolbarState.products.length
        ? toolbarState.products.map((p) => p.value)
        : undefined,
      excludeProductLevel: Boolean(toolbarState.allCommunity),
      orderBy: toolbarState.sort.order.field as OrganizationFeedsOrderBy,
      direction: toolbarState.direction as OrderDirection,
    },
    { fetchPolicy: "network-only" }
  )

  const { data, refetch } = usePaginationFragment<
    AdminMembersReportFeedsTablePaginationQuery,
    AdminMembersReportFeedsTable_PaginationFragment$key
  >(
    graphql`
      fragment AdminMembersReportFeedsTable_PaginationFragment on Organization
      @refetchable(queryName: "AdminMembersReportFeedsTablePaginationQuery")
      @argumentDefinitions(
        first: { type: "Int" }
        after: { type: "String" }
        last: { type: "Int" }
        before: { type: "String" }
        memberOrganizationMembershipId: { type: "ID!" }
        canRead: { type: "Boolean" }
        productIds: { type: "[ID!]" }
        excludeProductLevel: { type: "Boolean" }
        orderBy: { type: "OrganizationFeedsOrderBy" }
        direction: { type: "OrderDirection" }
      ) {
        id
        feeds(
          first: $first
          after: $after
          last: $last
          before: $before
          memberOrganizationMembershipId: $memberOrganizationMembershipId
          canRead: $canRead
          productIds: $productIds
          excludeProductLevel: $excludeProductLevel
          orderBy: $orderBy
          direction: $direction
        ) @connection(key: "AdminMembersReportFeedsTable_feeds") {
          totalCount
          edges {
            cursor
            node {
              id
              ...AdminMembersReportFeedsTableRowFragment
                @arguments(organizationMembershipId: $memberOrganizationMembershipId)
            }
          }
          pageInfo {
            endCursor
            startCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
    node
  )

  if (!data?.feeds) return null

  const feeds = Relay.connectionToArray(data.feeds)
  const cursorsList = data.feeds.edges.map((e) => e.cursor)

  return (
    <DiscoTable
      headerButtons={
        <div className={classes.headerButtonsContainer}>
          <DiscoText variant={"heading-xs-600"} className={classes.title}>
            {`Top Feed Contributions`}
          </DiscoText>
          <AdminMembersReportFeedsTableToolbar
            toolbarState={toolbarState}
            setToolbarState={setToolbarState}
          />
        </div>
      }
      headerFilterTags={
        (toolbarState.products.length || toolbarState.allCommunity) && (
          <AdminMembersReportFeedsTableFilterTags
            toolbarState={toolbarState}
            setToolbarState={
              setToolbarState as React.Dispatch<
                React.SetStateAction<Partial<AdminMembersReportFeedsTableToolbarState>>
              >
            }
          />
        )
      }
      filterDeps={[toolbarState]}
      activePage={activePage}
      setActivePage={setActivePage}
      rowsPerPage={FEEDS_PER_PAGE}
      scrollable
      testid={"AdminMembersReportFeedsTable"}
      header={headers}
      rows={feeds.map((feed) => (
        <AdminMembersReportFeedsTableRow key={feed.id} feedKey={feed} />
      ))}
      connection={{
        cursorsList,
        totalCount: data.feeds.totalCount,
        pageInfo: {
          endCursor: data.feeds.pageInfo.endCursor,
          startCursor: data.feeds.pageInfo.startCursor,
        },
      }}
      onPaginate={refetch}
    />
  )
}

const useStyles = makeUseStyles((theme) => ({
  title: { width: "100%", fontWeight: 600 },
  headerButtonsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: theme.spacing(1.5),

    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      justifyContent: "flex-start",
    },
  },
}))

function AdminMembersReportFeedsTableSkeleton() {
  const classes = useStyles()
  return (
    <DiscoTableSkeletonWithHeader
      headerButtons={
        <div className={classes.headerButtonsContainer}>
          <DiscoText variant={"heading-xs-600"} className={classes.title}>
            {`Top Feed Contributions`}
          </DiscoText>
          <AdminMembersReportFeedsTableToolbarSkeleton />
        </div>
      }
      header={headers}
      rows={FEEDS_PER_PAGE}
      row={<AdminMembersReportFeedsTableRowSkeleton />}
    />
  )
}

export default Relay.withSkeleton({
  component: AdminMembersReportFeedsTable,
  skeleton: AdminMembersReportFeedsTableSkeleton,
})
